import { TransactionByIdResponse } from "@services/models/Transactions/TransactionByIdModel";
import {
  DeleteWhitelistRequest,
  DeleteWhitelistResponse,
} from "@services/models/UserManagement/delete-whitelist";
import { GetKybResponse } from "@services/models/UserManagement/get-kyb";
import {
  GetKycRequest,
  GetKycResponse,
} from "@services/models/UserManagement/get-kyc";
import { GetNotificationsSettingsResponse } from "@services/models/UserManagement/get-notification-settings";
import { GetSecuritySettingsResponse } from "@services/models/UserManagement/get-security-settings";
import {
  GetDefaultConfigResponse,
  PutConfigRequest,
} from "@services/models/UserManagement/get-services";

import {
  GetAllPersonalTransaction,
  GetAllPersonalTransactionRequest,
} from "@services/models/UserManagement/get-transaction";
import { GetUserBalancesResponse } from "@services/models/UserManagement/get-wallets";
import { GetWhitelistAddressResponse } from "@services/models/UserManagement/get-whitelist-data-address";
import { GetWhitelistIPResponse } from "@services/models/UserManagement/get-whitelist-data-ip";
import { GetWhitelistUserResponse } from "@services/models/UserManagement/get-whitelist-data-user";
import { PaymentMethodsResponse } from "@services/models/UserManagement/payment-methods";
import { RevokeSessionRequest } from "@services/models/UserManagement/revoke-session";
import {
  ToggleWhitelistActivityRequest,
  ToggleWhitelistActivityResponse,
} from "@services/models/UserManagement/toggle-whitelist-activity";
import {
  UpdateNotificationSettingRequest,
  UpdateNotificationSettingResponse,
} from "@services/models/UserManagement/update-notification-settings";
import {
  UpdateSecuritySettingRequest,
  UpdateSecuritySettingResponse,
} from "@services/models/UserManagement/update-security-settings";
import {
  UpdataUserProfilePayload,
  UpdataUserProfileResponse,
} from "@services/models/UserManagement/update-user-profile";
import { UserProfileResponse } from "@services/models/UserManagement/user";
import {
  UsersRequest,
  UsersResponse,
} from "@services/models/UserManagement/users";
import { createApiClient } from "../api/createApiClient";
import { AxiosInstance } from "axios";

class UserManagementApiService {
  private api: AxiosInstance;
  constructor() {
    this.api = createApiClient();
  }

  public async getUsers(data: UsersRequest): Promise<UsersResponse> {
    try {
      const response = await this.api.get("/adminPanel/userManagement/users", {
        params: {
          page: data.page,
          limit: data.limit,
          type: data.type,
          search: data.search,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getUserDetails(
    userId: number,
    accountType: string,
  ): Promise<UserProfileResponse> {
    try {
      const response = await this.api.get(
        `/adminPanel/userManagement/user/${userId}`,
        {
          params: {
            accountType: accountType,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getPaymentMethods(
    userId: number,
  ): Promise<PaymentMethodsResponse> {
    try {
      const response = await this.api.get(
        `/adminPanel/userManagement/user/${userId}/payment-methods`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getSecuritySettings(
    userId: number,
  ): Promise<GetSecuritySettingsResponse> {
    try {
      const response = await this.api.get(
        `/adminPanel/userManagement/user/${userId}/security-settings`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getNotificationSettings(
    userId: number,
  ): Promise<GetNotificationsSettingsResponse> {
    try {
      const response = await this.api.get(
        `/adminPanel/userManagement/user/${userId}/notification-settings`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getActiveSessions(userId: number): Promise<any> {
    try {
      const response = await this.api.get(
        `/adminPanel/userManagement/user/${userId}/active-sessions`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async revokeSession(request: RevokeSessionRequest): Promise<any> {
    try {
      const response = await this.api.post(
        `/adminPanel/userManagement/user/${request.userId}/revoke-session`,
        {
          revokeType: request.revokeType,
          revokeId: request.revokeId,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getKycDetails(data?: GetKycRequest): Promise<GetKycResponse> {
    try {
      const params: Record<string, any> = {};

      const response = await this.api.get<GetKycResponse>(
        `/adminPanel/userManagement/user/${data?.userId}/kyc`,
        {
          params,
        },
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getKybDetails(userId: number): Promise<GetKybResponse> {
    try {
      const response = await this.api.get<GetKybResponse>(
        `/adminPanel/userManagement/user/${userId}/kyb`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getWhitelistDataIp(
    userId: number,
  ): Promise<GetWhitelistIPResponse> {
    try {
      const response = await this.api.get(
        `/adminPanel/userManagement/user/${userId}/whitelist-data?type=0`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getWhitelistDataAddress(
    userId: number,
  ): Promise<GetWhitelistAddressResponse> {
    try {
      const response = await this.api.get(
        `/adminPanel/userManagement/user/${userId}/whitelist-data?type=1`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getWhitelistDataUser(
    userId: number,
  ): Promise<GetWhitelistUserResponse> {
    try {
      const response = await this.api.get(
        `/adminPanel/userManagement/user/${userId}/whitelist-data?type=2`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async deleteWhitelist(
    data: DeleteWhitelistRequest,
  ): Promise<DeleteWhitelistResponse> {
    try {
      const response = await this.api.delete<DeleteWhitelistResponse>(
        `/adminPanel/userManagement/user/${data.userId}/delete-whitelist`,
        {
          data: {
            type: data.type,
            id: data.id,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async toggleWhitelistActivity(
    data: ToggleWhitelistActivityRequest,
  ): Promise<ToggleWhitelistActivityResponse> {
    try {
      const response = await this.api.put<ToggleWhitelistActivityResponse>(
        `/adminPanel/userManagement/user/${data.userId}/toggle-whitelist-activity`,
        {
          type: data.type,
          id: data.id,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getWallets(userId: number): Promise<GetUserBalancesResponse> {
    try {
      const response = await this.api.get(
        `/adminPanel/userManagement/user/${userId}/wallets`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async updateUserProfile(
    data: UpdataUserProfilePayload,
  ): Promise<UpdataUserProfileResponse> {
    try {
      const response = await this.api.put<UpdataUserProfileResponse>(
        `/adminPanel/userManagement/user/${data.userId}/update-profile`,
        data,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async updateSecuritySettings(
    data: UpdateSecuritySettingRequest,
  ): Promise<UpdateSecuritySettingResponse> {
    try {
      const response = await this.api.put<UpdateSecuritySettingResponse>(
        `/adminPanel/userManagement/user/${data.userId}/update-security-settings`,
        data,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async updateNotificationSettings(
    data: UpdateNotificationSettingRequest,
  ): Promise<UpdateNotificationSettingResponse> {
    try {
      const response = await this.api.put<UpdateNotificationSettingResponse>(
        `/adminPanel/userManagement/user/${data.userId}/update-notification-settings`,
        data,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getUserTransactions(
    request: GetAllPersonalTransactionRequest,
  ): Promise<GetAllPersonalTransaction> {
    try {
      const params: any = {
        rows: request.rows,
        page: request.page,
      };

      if (request.merchantId) {
        params.merchantId = request.merchantId;
      }
      // if (request.ledger) {
      //   params.ledger = request.ledger;
      // }
      // if (request.startDate && request.endDate) {
      //   params.startDate = request.startDate;
      //   params.endDate = request.endDate;
      // }
      // if (request.searchTerm) {
      //   params.searchTerm = request.searchTerm;
      // }

      const response = await this.api.get(
        `/adminPanel/userManagement/user/${request.userId}/get-transaction-history`,
        { params },
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getUserTransactionsDetails(
    userId: number,
    transactionId: string,
    merchantId?: number,
  ): Promise<TransactionByIdResponse> {
    try {
      const params: any = {
        transactionId,
      };

      if (merchantId !== undefined && merchantId !== -1 && merchantId !== 0) {
        params.merchantId = merchantId;
      }

      const response = await this.api.get(
        `/adminPanel/userManagement/user/${userId}/get-transaction-details`,
        { params },
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getUserConfig({
    userId,
  }: {
    userId: number;
  }): Promise<GetDefaultConfigResponse> {
    try {
      const params: any = {
        userId,
      };
      const response = await this.api.get(`/adminPanel/config`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async updateUserConfig(
    data: PutConfigRequest,
  ): Promise<GetDefaultConfigResponse> {
    try {
      const response = await this.api.put(`/adminPanel/config`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export const userManagementApis = new UserManagementApiService();
